<script setup lang="ts">
// First-run pairing screen. Admin creates the device in the dashboard, copies
// the bearer token, and pastes it here. Also stores the API base URL so the
// game works against different environments (dev/staging/prod).
import { ref } from 'vue';
import { apiBase, setApiBase, setDeviceToken } from '../lib/api';
import { useGameStore } from '../stores/game';

const store = useGameStore();
const base = ref(apiBase());
const token = ref('');
const error = ref('');
const saving = ref(false);

async function save() {
  error.value = '';
  if (!base.value.trim() || !token.value.trim()) {
    error.value = 'Both fields required';
    return;
  }
  saving.value = true;
  setApiBase(base.value.trim().replace(/\/$/, ''));
  setDeviceToken(token.value.trim());
  await store.init();
  saving.value = false;
  if (store.loadError) {
    error.value = store.loadError;
  }
}
</script>

<template>
  <div class="setup">
    <div class="setup-card">
      <div class="wsb-logo">WSB</div>
      <div class="wsb-sub">WORLD SPORTS BETTING</div>
      <h2>Device Setup</h2>
      <p class="hint">Get the API URL and device token from the WSB admin dashboard.</p>
      <label>API URL</label>
      <input v-model="base" type="text" placeholder="https://wsb-api.workers.dev" />
      <label>Device Token</label>
      <input v-model="token" type="text" placeholder="paste token from dashboard" />
      <div v-if="error" class="err">{{ error }}</div>
      <button class="btn btn-gold" :disabled="saving" @click="save">
        {{ saving ? 'Verifying…' : 'Pair Device' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.setup {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 20px;
}
.setup-card {
  background: var(--dark2);
  border: 1px solid var(--or-dk);
  border-radius: var(--r);
  padding: 36px 28px;
  width: 100%;
  max-width: 360px;
  box-shadow: var(--sh);
}
.wsb-logo {
  font-size: 30px;
  font-weight: 900;
  color: var(--or);
  letter-spacing: 3px;
  text-align: center;
}
.wsb-sub {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  letter-spacing: 4px;
  text-align: center;
  margin-bottom: 22px;
}
h2 {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 6px;
}
.hint {
  font-size: 12px;
  color: var(--grey);
  margin-bottom: 18px;
}
label {
  display: block;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.55);
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 12px 0 4px;
}
.err {
  color: var(--red);
  font-size: 13px;
  margin: 10px 0;
}
.btn {
  width: 100%;
  margin-top: 18px;
}
.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
